import { Injectable } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators, FormControl } from '@angular/forms';
import { AdditionalLocation, CustomerInfoData } from 'src/app/core/models/customer';

@Injectable({
  providedIn: 'root',
})
export class CustomerInfoFormService {
  constructor(private fb: FormBuilder) {}

  createCustomerInfoForm(data?: CustomerInfoData): FormGroup<CustomerInfoForm> {
    const form = this.fb.group<CustomerInfoForm>({
      customerName: this.fb.control('', [Validators.required]),
      friendlyName: this.fb.control('', [Validators.required]),
      preferredGoLiveDate: this.fb.control<Date | null>(null, [Validators.required]),
      selectedServices: this.fb.control<string[]>([], [Validators.required, Validators.minLength(1)]),
      slaLevel: this.fb.control(null, [Validators.required]),
      shippingContactName: this.fb.control(''),
      shippingContactPhone: this.fb.control(''),
      shippingAddress: this.fb.control(''),
      shippingState: this.fb.control(''),
      shippingCity: this.fb.control(''),
      shippingZipCode: this.fb.control(''),
      additionalLocations: this.fb.array<FormGroup>([]),
    });

    // Initialize with data if provided
    if (data) {
      this.patchFormWithData(form, data);
    }

    return form;
  }

  private patchFormWithData(form: FormGroup<CustomerInfoForm>, data: CustomerInfoData): void {
    form.patchValue({
      customerName: data.customerName,
      friendlyName: data.friendlyName,
      preferredGoLiveDate: data.preferredGoLiveDate,
      selectedServices: data.selectedServices,
      slaLevel: this.findDropdownObject(this.getSlaOptions(), data.slaLevel),
      shippingContactName: data.shippingContactName,
      shippingContactPhone: data.shippingContactPhone,
      shippingAddress: data.shippingAddress,
      shippingState: data.shippingState,
      shippingCity: data.shippingCity,
      shippingZipCode: data.shippingZipCode,
    });

    // Initialize additional locations if they exist
    if (data.additionalLocations && data.additionalLocations.length > 0) {
      const additionalLocations = form.get('additionalLocations') as FormArray;
      data.additionalLocations.forEach((location) => {
        additionalLocations.push(this.createLocationFormGroup(location));
      });
    }
  }

  createLocationFormGroup(location?: Partial<AdditionalLocation>): FormGroup {
    return this.fb.group({
      contactName: [location?.contactName || ''],
      contactPhone: [location?.contactPhone || ''],
      address: [location?.address || ''],
      state: [location?.state || ''],
      city: [location?.city || ''],
      zipCode: [location?.zipCode || ''],
    });
  }

  addAdditionalLocation(form: FormGroup<CustomerInfoForm>): void {
    const additionalLocations = form.get('additionalLocations') as FormArray;
    additionalLocations.push(this.createLocationFormGroup());
  }

  removeAdditionalLocation(form: FormGroup<CustomerInfoForm>, index: number): void {
    const additionalLocations = form.get('additionalLocations') as FormArray;
    additionalLocations.removeAt(index);
  }

  getFormData(form: FormGroup<CustomerInfoForm>): CustomerInfoData {
    const value = form.value;
    return {
      customerName: value.customerName ?? '',
      friendlyName: value.friendlyName ?? '',
      preferredGoLiveDate: value.preferredGoLiveDate ?? null,
      selectedServices: value.selectedServices ?? [],
      slaLevel: this.getDropdownValue(value.slaLevel) ?? '',
      shippingContactName: value.shippingContactName ?? '',
      shippingContactPhone: value.shippingContactPhone ?? '',
      shippingAddress: value.shippingAddress ?? '',
      shippingState: value.shippingState ?? '',
      shippingCity: value.shippingCity ?? '',
      shippingZipCode: value.shippingZipCode ?? '',
      additionalLocations: (value.additionalLocations ?? []) as AdditionalLocation[],
    };
  }

  // Helper methods for dropdown value handling
  getDropdownValue(value: any): string {
    if (typeof value === 'string') {
      return value;
    }
    if (value && typeof value === 'object' && 'value' in value) {
      return value.value;
    }
    return '';
  }

  findDropdownObject(options: any[], value: string): any {
    if (!value) return null;
    return options.find((option) => option.value === value) || null;
  }

  // Current static data methods, maybe we can get this from the backend?
  getAvailableServices() {
    return [
      {
        value: 'mxdr',
        label: 'mXDR (Extended Detection & Response)',
        icon: 'securityCheckOutlineIcon',
        iconBackground: '#e3f2fd', // Light blue
        description: 'Extended Detection & Response with advanced threat hunting',
      },
      {
        value: 'medr',
        label: 'mEDR (Endpoint Detection & Response)',
        icon: 'laptopOutlineIcon',
        iconBackground: '#f3e5f5', // Light purple
        description: 'Endpoint Detection & Response for device protection',
      },
      {
        value: 'mvss',
        label: 'VSS (Vulnerability Scanning Service)',
        icon: 'bugOutlineIcon',
        iconBackground: '#fff3e0', // Light orange
        description: 'Vulnerability scanning and assessment services',
      },
      {
        value: 'mes',
        label: 'mES (Email Security)',
        icon: 'envelopIcon',
        iconBackground: '#e8f5e8', // Light green
        description: 'Email security and threat protection',
      },
      {
        value: 'professional',
        label: 'Professional Services',
        icon: 'userIcon',
        iconBackground: '#fce4ec', // Light pink
        description: 'Professional services including penetration testing and consulting',
      },
    ];
  }

  getSlaOptions() {
    return [
      { value: 'advanced', text: 'Advanced (Standard)' },
      { value: 'premier', text: 'Premier' },
    ];
  }
}

export interface CustomerInfoForm {
  customerName: FormControl<string | null>;
  friendlyName: FormControl<string | null>;
  preferredGoLiveDate: FormControl<Date | null>;
  selectedServices: FormControl<string[] | null>;
  slaLevel: FormControl<any>;
  shippingContactName: FormControl<string | null>;
  shippingContactPhone: FormControl<string | null>;
  shippingAddress: FormControl<string | null>;
  shippingState: FormControl<string | null>;
  shippingCity: FormControl<string | null>;
  shippingZipCode: FormControl<string | null>;
  additionalLocations: FormArray<FormGroup>;
}
